import { Warning } from "@phosphor-icons/react"
import { Badge, type BadgeProps } from "~/components/reusable/Badge"
import { Tooltip } from "~/components/reusable/Tooltip"
import { formatName } from "~/utils/namingUtils"
import { sortSizesComparator } from "~/utils/sizeSplit"
import {
  HISTORIC_DATA_LABEL,
  IMPUTATION_WARNING_THRESHOLD,
  getImputationWarningText,
  trustVariantMap,
  confidenceConfig,
  confidenceLevels,
  type ConfidenceLevel,
} from "~/utils/sizeSplitConfidence"

type ImputedSize = { size: string; fraction: number; variantName: string }

type Props = {
  isHistoric?: boolean
  confidenceScore?: string | null
  count?: number
  short?: boolean
  missingDataText?: string
  imputedFraction?: number | null
  imputedSizes?: ImputedSize[]
  multipleVariantsSelected?: boolean
  size?: BadgeProps["size"]
}

export function ConfidenceBadge({
  isHistoric = false,
  confidenceScore,
  count,
  short = false,
  missingDataText,
  imputedFraction,
  imputedSizes,
  multipleVariantsSelected = false,
  size,
}: Props) {
  const rawLevel = isHistoric ? HISTORIC_DATA_LABEL : confidenceScore
  const level: ConfidenceLevel | null =
    rawLevel != null && (confidenceLevels as readonly string[]).includes(rawLevel)
      ? (rawLevel as ConfidenceLevel)
      : null

  if (!level) return null

  const config = confidenceConfig[level]
  const variant = trustVariantMap[level]
  const baseLabel = isHistoric ? level : short ? level : `${level} confidence`
  const label = count !== undefined ? `${count}x ${baseLabel}` : baseLabel

  const affectedSizes = (
    imputedSizes?.filter((s) => s.fraction > IMPUTATION_WARNING_THRESHOLD) ?? []
  ).sort((a, b) => sortSizesComparator(a.size, b.size))
  const hasImputationWarning =
    affectedSizes.length > 0 ||
    (imputedFraction != null && imputedFraction > IMPUTATION_WARNING_THRESHOLD)

  const imputationTooltipContent = (() => {
    if (!hasImputationWarning) return null
    if (affectedSizes.length > 0) {
      const byVariant = Map.groupBy(affectedSizes, (s) => s.variantName)
      const multipleVariants = multipleVariantsSelected || byVariant.size > 1
      return (
        <div className="space-y-1">
          <p className="font-medium text-fg-base">Estimated demand during stockout</p>
          <p className="paragraph-sm text-fg-muted">
            A high share of estimated demand means we have lower confidence in the size split for
            the following SKUs:
          </p>
          {multipleVariants ? (
            <ul className="paragraph-sm text-fg-muted space-y-1 list-disc list-inside">
              {Array.from(byVariant.entries()).map(([variantName, sizes]) => (
                <li key={variantName}>
                  <span className="text-fg-base">{variantName}</span>
                  <ul className="list-[circle] list-inside pl-3 space-y-0.5">
                    {sizes.map((s) => (
                      <li key={s.size}>
                        {s.size}: {Math.round(s.fraction * 100)}% estimated{" "}
                        {formatName("sale", true)}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="paragraph-sm text-fg-muted list-[circle] list-inside space-y-0.5">
              {affectedSizes.map((s) => (
                <li key={s.size}>
                  {s.size}: {Math.round(s.fraction * 100)}% estimated {formatName("sale", true)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )
    }
    return (
      <div className="space-y-1">
        <p className="font-medium text-fg-base">Estimated demand during stockout</p>
        <p className="paragraph-sm text-fg-muted">{getImputationWarningText(imputedFraction!)}</p>
      </div>
    )
  })()

  return (
    <Tooltip
      side="bottom"
      body={
        <div className="text-left space-y-2">
          <p className="font-medium text-fg-base">Size Split Confidence: {level}</p>
          <p className="paragraph-sm text-fg-muted">{config.description}</p>
          <p className="paragraph-sm text-fg-muted">
            <span className="font-medium text-fg-base">Recommendation</span>
            <br />
            {missingDataText
              ? missingDataText
              : hasImputationWarning
                ? "Review the size split before placing purchase orders. Some SKUs have a high share of estimated sales from past or historic stockouts."
                : config.recommendation}
          </p>
          {imputationTooltipContent}
        </div>
      }
    >
      <Badge variant={hasImputationWarning ? "yellow" : variant} size={size}>
        {label}
        {hasImputationWarning && <Warning className="ml-1 shrink-0" size={12} weight="fill" />}
      </Badge>
    </Tooltip>
  )
}
